import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'
import { setCategory } from '@/store/slices/categorySlice'
import { setTypes } from '@/store/slices/speakerTypesSlice'
import { setDefaultPageSeo } from '@/store/slices/headSlice'
import { useMsal } from '@azure/msal-react'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { EventType } from '@azure/msal-browser'
import useCategoriesService from '@/hooks/useCategoriesService'
import Logo from '@/components/common/Logo'
import HeaderMobileView from '@/components/common/HeaderMobileView'
import MyProfile from '@/components/speaker/MyProfile'
import IdleTimer from '@/components/common/IdleTimerContainer'
import CategoriesDropdown from './speaker/CategoriesDropdown'
import axios from 'axios'

const NavBar = ({ setIsLoading }) => {
  const { asPath } = useRouter()
  const dispatch = useDispatch()
  const [categories, setCategories] = useState(null)
  const { data, isLoading, isError, isSuccess } = useCategoriesService()
  const loggedInUserDetails = JSON.parse(localStorage.getItem('userDetails')) || {}
  const name = JSON.parse(localStorage.getItem('updatedUserName')) || ''
  const { instance, accounts } = useMsal()
  const accountName = accounts[0]?.name.split(' ')

  useEffect(() => {
    instance.addEventCallback(async (event) => {
      if (event.eventType === EventType.LOGIN_START) {
        setIsLoading(true)
      } else {
        setIsLoading(false)
      }
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const account = event.payload
        account && localStorage.setItem('account', JSON.stringify(account))

        const url = `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/profile`
        const config = {
          headers: {
            'Ocp-Apim-Subscription-Key': process.env.NEXT_PUBLIC_APIM_SUBSCRIPTION_KEY,
            Authorization: `Bearer ${account?.idToken}`,
          },
        }
        await axios.get(url, config).then((res) => {
          const result = { userDetails: res.data.data, access_token: account?.idToken }
          localStorage.setItem('userDetails', JSON.stringify(result))
          localStorage.setItem('login-event', 'started')
          localStorage.removeItem('login-event')
        })
      }
    })
  }, [])

  useEffect(() => {
    if (isSuccess) {
      dispatch(setCategory(data))
      setCategories(data)
    }
  }, [isSuccess])

  useEffect(() => {
    const getSpeakerTypes = async () => {
      const url = `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/speakerTypes`
      const config = {
        headers: {
          'Ocp-Apim-Subscription-Key': process.env.NEXT_PUBLIC_APIM_SUBSCRIPTION_KEY,
        },
      }
      const response = await fetch(url, config)
      const data = await response.json()
      dispatch(setTypes(data?.data))
    }
    getSpeakerTypes()
  }, [])

  useEffect(() => {
    if (asPath) {
      if (!/^.*speaker/.test(asPath)) dispatch(setDefaultPageSeo())
    }
  }, [asPath])

  return (
    <>
      <header>
        <div className="uta-container">
          <nav className="flex items-center justify-between shadow-sm bg-black relative md:py-6 xs:py-4 py-2">
            <a href="#" className="skip-to-main-content-link">
              Skip to main content
            </a>
            <div className="flex items-center justify-between">
              <Logo />
            </div>
            <ul className="justify-between mt-12 md:flex md:mt-0 hidden leading-[35px]">
              <div className="justify-center flex-1 order-1 space-y-5 md:flex md:space-x-6 md:space-y-0">
                <CategoriesDropdown
                  view="Desktop"
                  categories={categories}
                  isLoading={isLoading}
                  isError={isError}
                />

                <li className="order-2 pb-5 md:pb-0" tabIndex={0}>
                  {loggedInUserDetails?.userDetails ? (
                    <Link href="/myAccount" legacyBehavior>
                      <a className="focus:outline-none tagline-font">
                        <button className="focus:outline-none">
                          <MyProfile
                            userName={
                              name
                                ? name
                                : loggedInUserDetails?.userDetails?.firstName +
                                  ' ' +
                                  loggedInUserDetails?.userDetails?.lastName?.slice(0, 1) +
                                  '.'
                            }
                          />
                        </button>
                      </a>
                    </Link>
                  ) : (
                    <>
                      <AuthenticatedTemplate>
                        <Link href="/myAccount" legacyBehavior>
                          <a className="focus:outline-none tagline-font">
                            <button className="focus:outline-none">
                              <MyProfile
                                userName={
                                  accountName &&
                                  accountName[0] + ' ' + accountName[1].slice(0, 1) + '.'
                                }
                              />
                            </button>
                          </a>
                        </Link>
                      </AuthenticatedTemplate>
                      <UnauthenticatedTemplate>
                        <button
                          onClick={() => instance?.loginRedirect()}
                          className="text-sm tracking-wider tagline-font focus:outline-1"
                        >
                          Login
                        </button>
                      </UnauthenticatedTemplate>
                    </>
                  )}
                </li>
              </div>
            </ul>
            <div className="flex items-center gap-6 my-auto md:hidden">
              <CategoriesDropdown
                view="Mobile"
                categories={categories}
                isLoading={isLoading}
                isError={isError}
              />
              <div>
                <HeaderMobileView />
              </div>
            </div>
          </nav>
        </div>
      </header>
      {accounts?.length > 0 && <IdleTimer user={accounts} />}
    </>
  )
}

export default NavBar
